import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAgentGroups } from '@/features/agent-groups/api';
import {
  type InboundComposeFormValues,
  type InboundGroupRow,
} from '../../types';
import { DTMF_OPTIONS } from './shared-utils';

interface Props {
  values: InboundComposeFormValues;
  setValues: (patch: Partial<InboundComposeFormValues>) => void;
}

export function StepRouting({ values, setValues }: Props) {
  const groups = useAgentGroups();

  const [pickGroupId, setPickGroupId] = useState('');
  const [pickDtmf, setPickDtmf] = useState('');

  const takenDtmf = new Set(values.groupRows.map((r) => r.agentDtmf));
  const availableGroupDtmfs = values.dtmf.filter((d) => !takenDtmf.has(d));

  const addGroupRow = () => {
    if (!pickGroupId || !pickDtmf) {
      toast.error('Pick an agent group and a DTMF digit.');
      return;
    }
    if (takenDtmf.has(pickDtmf)) return;
    const group = (groups.data ?? []).find(
      (g) => String(g.groupId) === pickGroupId,
    );
    if (!group) return;
    setValues({
      groupRows: [
        ...values.groupRows,
        {
          groupId: pickGroupId,
          groupName: group.groupName,
          agentDtmf: pickDtmf,
        } as InboundGroupRow,
      ],
    });
    setPickGroupId('');
    setPickDtmf('');
  };

  const removeGroupRow = (dtmf: string) =>
    setValues({
      groupRows: values.groupRows.filter((r) => r.agentDtmf !== dtmf),
    });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-1">
            DTMF<span className="text-destructive">*</span>
          </CardTitle>
          <CardDescription>
            Pick which DTMF digits the menu accepts.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MultiSelect
            options={DTMF_OPTIONS}
            value={values.dtmf}
            onChange={(v) => {
              // Drop any group rows whose DTMF is no longer valid.
              const remaining = values.groupRows.filter((r) =>
                v.includes(r.agentDtmf),
              );
              setValues({ dtmf: v, groupRows: remaining });
            }}
            placeholder="Pick accepted DTMF digits"
            searchPlaceholder="Filter…"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Patch agent groups</CardTitle>
          <CardDescription>
            Bind one agent group to each accepted DTMF digit. Callers landing
            on that digit will be patched to that group.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px_auto] sm:items-end rounded-md border border-border p-3">
            <div className="space-y-1.5">
              <Label htmlFor="group">
                Agent group
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Select value={pickGroupId} onValueChange={setPickGroupId}>
                <SelectTrigger id="group">
                  <SelectValue
                    placeholder={
                      groups.isLoading
                        ? 'Loading…'
                        : (groups.data ?? []).length === 0
                          ? 'No agent groups — create one first'
                          : 'Pick an agent group'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {(groups.data ?? []).map((g) => (
                    <SelectItem key={g.groupId} value={String(g.groupId)}>
                      {g.groupName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dtmf">
                DTMF
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Select
                value={pickDtmf}
                onValueChange={setPickDtmf}
                disabled={availableGroupDtmfs.length === 0}
              >
                <SelectTrigger id="dtmf">
                  <SelectValue
                    placeholder={
                      values.dtmf.length === 0
                        ? 'Pick DTMF above'
                        : availableGroupDtmfs.length === 0
                          ? 'All digits bound'
                          : 'Digit'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableGroupDtmfs.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              onClick={addGroupRow}
              disabled={!pickGroupId || !pickDtmf}
            >
              <Plus className="h-4 w-4" />
              Bind
            </Button>
          </div>

          {values.groupRows.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-muted/20 py-6 text-center text-sm text-muted-foreground">
              No groups bound yet. Pick a DTMF digit and an agent group, then
              click Bind.
            </div>
          ) : (
            <ol className="space-y-2">
              {values.groupRows.map((r) => (
                <li
                  key={r.agentDtmf}
                  className="flex items-center gap-3 rounded-lg border border-border bg-card p-3"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-base font-bold text-primary-foreground">
                    {r.agentDtmf}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                      {r.groupName}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Group #{r.groupId}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => removeGroupRow(r.agentDtmf)}
                    aria-label="Remove binding"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
